/**
 * Word Form Store — the buyer Word form fill session (R3), plus the schedules
 * found inside tender files.
 *
 * Flow: load a `word_form` artifact → wait out ingest (poll) → review fill
 * proposals per schedule → export a completed copy of the buyer's file.
 *
 * Mirrors `schedule-store`: decisions are NOT optimistic (the server's claim is
 * the authority), and every poll continuation is epoch-guarded so switching
 * forms or resetting can never be clobbered by an in-flight loop.
 */

import { create } from 'zustand'
import {
  ApiRequestError,
  correctFormSchedules,
  createManualDocxFill,
  createWordForm,
  decideAllFillProposals,
  decideFillProposal,
  exportWordForm,
  getBidSchedules,
  getDocumentSchedules,
  getFillProposals,
  getWordForm,
  getWordFormExports,
  reingestWordForm,
  type BidArtifact,
  type BidScheduleDocument,
  type WordFormInfo,
} from '@/lib/api'
import {
  docxFieldTarget,
  fillRefusalMessage,
  manualFillValue,
  reasonOf,
  scheduleCorrectionMessage,
} from '@/lib/word-form'
import type { FillDecision, FillProposal, FillProposalCounts } from '@/types/schedule'
import type {
  FormField,
  FormHeading,
  FormSchedule,
  FormScheduleCorrection,
  OutputVersionWithUrl,
  WordFormExportResult,
  WordFormMetadata,
} from '@/types/word-form'

export const WORD_FORM_POLL_INTERVAL_MS = 3000
/** ~5 minutes. Ingest takes seconds; past this it is stuck. */
const MAX_POLL_ATTEMPTS = 100

/** Selection key for fields that sit outside every found schedule. */
export const UNSCHEDULED_KEY = 'unscheduled'

const LOAD_ERROR = "Couldn't load this form — try again."
const TIMEOUT_ERROR = 'Reading this form is taking longer than expected — try again in a moment.'

/** The gate refused the completed copy (422 `FORM_CHECK_FAILED`). */
export const FORM_CHECK_FAILED_MESSAGE =
  "We couldn't produce a safe copy of this form. Nothing was changed."
/** 400 with `details.rejected`: every accepted answer was refused. */
export const NOTHING_WRITABLE_MESSAGE =
  "None of your accepted answers could be written into this form. Check the answers marked below and try again."
const EXPORT_FAILED_MESSAGE = "Couldn't make the completed copy. Try again."
const CREATE_FAILED_MESSAGE = "Couldn't set up this form. Try again."

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function messageOf(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : ''
  return message.length > 0 ? message : fallback
}

/** Counts after a decision, without a round trip. Same rules as `schedule-store`. */
function countProposals(proposals: FillProposal[]): FillProposalCounts {
  const counts: FillProposalCounts = { pending: 0, accepted: 0, dismissed: 0, applied: 0 }
  for (const proposal of proposals) {
    if (proposal.status === 'pending') counts.pending++
    else if (proposal.status === 'dismissed') counts.dismissed++
    else if (proposal.status === 'accepted') {
      if (proposal.appliedAt === null) counts.accepted++
      else counts.applied++
    }
  }
  return counts
}

/** First schedule, or the unscheduled bucket when nothing was found. */
function defaultSelection(schedules: FormSchedule[]): string {
  return schedules[0]?.id ?? UNSCHEDULED_KEY
}

/** Keep the current selection while it still exists. */
function keepSelection(current: string | null, schedules: FormSchedule[]): string {
  if (current === UNSCHEDULED_KEY) return current
  if (current && schedules.some((s) => s.id === current)) return current
  return defaultSelection(schedules)
}

export interface DocumentSchedulesEntry {
  status: 'loading' | 'ready' | 'error'
  schedules: FormSchedule[]
  wordFormArtifactId: string | null
}

/** One call's worth of schedule counts for a bid's Tender files list. */
export interface BidSchedulesSummary {
  bidId: string
  status: 'loading' | 'ready' | 'error'
  /** Only documents with at least one schedule. */
  byDocument: Record<string, BidScheduleDocument>
}

/** Why the last "Download completed copy" made no file, and whether retrying can help. */
export interface ExportProblem {
  message: string
  canRetry: boolean
}

export interface CreatedForm {
  artifactId: string
  /** The document already had a form; this is it. */
  existing: boolean
}

interface WordFormState {
  artifactId: string | null
  artifact: BidArtifact | null
  metadata: WordFormMetadata | null
  schedules: FormSchedule[]
  fields: FormField[]
  headings: FormHeading[]
  proposals: FillProposal[]
  counts: FillProposalCounts | null
  exports: OutputVersionWithUrl[]
  /** A schedule id, or `UNSCHEDULED_KEY`. */
  selectedScheduleId: string | null
  isLoading: boolean
  isExporting: boolean
  isCorrecting: boolean
  error: string | null
  exportProblem: ExportProblem | null
  /** Why the last schedule correction was refused, in plain words. */
  correctionError: string | null
  /** Schedules found in tender files, keyed by document id (the panel). */
  documentSchedules: Record<string, DocumentSchedulesEntry>
  /** Schedule counts for the Tender files list (one call per bid). */
  bidSchedules: BidSchedulesSummary | null
  /** Document id a form is being created for. */
  creatingFormFor: string | null

  load: (artifactId: string) => Promise<void>
  selectSchedule: (scheduleId: string) => void
  refreshProposals: () => Promise<void>
  refreshExports: () => Promise<void>
  decide: (proposalId: string, decision: FillDecision, editedValue?: string) => Promise<void>
  /**
   * Save an answer typed into a field with no proposal. Resolves null when
   * saved, otherwise the plain reason it was refused.
   */
  saveManualAnswer: (field: FormField, draft: string) => Promise<string | null>
  /** Accept every pending company-profile answer in one schedule — one call. */
  acceptAllFromProfile: (scheduleId: string) => Promise<void>
  /** The export result (with its link), or null when no file was made (see `exportProblem`). */
  doExport: () => Promise<WordFormExportResult | null>
  reingest: () => Promise<void>
  /** Apply a schedule correction. Returns success. */
  correctSchedules: (artifactId: string, correction: FormScheduleCorrection) => Promise<boolean>
  /** Schedule counts for a bid's Tender files list. Once per bid unless `force`. */
  loadBidSchedules: (bidId: string, force?: boolean) => Promise<void>
  /** Load (once) the schedules found in a tender file. `force` refetches. */
  loadDocumentSchedules: (bidId: string, documentId: string, force?: boolean) => Promise<void>
  /** Create (or find) the form for a Word bid document. Null on failure (see `error`). */
  createForm: (bidId: string, documentId: string, name: string) => Promise<CreatedForm | null>
  clearError: () => void
  clearExportProblem: () => void
  clearCorrectionError: () => void
  reset: () => void
}

const initialSession = {
  artifactId: null,
  artifact: null,
  metadata: null,
  schedules: [],
  fields: [],
  headings: [],
  proposals: [],
  counts: null,
  exports: [],
  selectedScheduleId: null,
  isLoading: false,
  isExporting: false,
  isCorrecting: false,
  error: null,
  exportProblem: null,
  correctionError: null,
} satisfies Partial<WordFormState>

let pollEpoch = 0

const FILL_REFUSED_NOT_READY = 'This form is still being read. Try again in a moment.'

/** What a failed export means for the person, from the server's answer. */
function exportProblemOf(error: unknown): ExportProblem {
  if (error instanceof ApiRequestError) {
    if (error.statusCode === 422 || error.code === 'FORM_CHECK_FAILED') {
      return { message: FORM_CHECK_FAILED_MESSAGE, canRetry: true }
    }
    if (error.statusCode === 400) {
      const rejected = error.details?.['rejected']
      if (Array.isArray(rejected) && rejected.length > 0) {
        return { message: NOTHING_WRITABLE_MESSAGE, canRetry: false }
      }
      return { message: messageOf(error, EXPORT_FAILED_MESSAGE), canRetry: false }
    }
  }
  return { message: EXPORT_FAILED_MESSAGE, canRetry: true }
}

/** The Tender files counts after a form was created for a document. */
function withFormArtifact(
  summary: BidSchedulesSummary | null,
  documentId: string,
  artifactId: string
): BidSchedulesSummary | null {
  const entry = summary?.byDocument[documentId]
  if (!summary || !entry) return summary
  return {
    ...summary,
    byDocument: { ...summary.byDocument, [documentId]: { ...entry, wordFormArtifactId: artifactId } },
  }
}

/** The Tender files counts after a correction changed a form's schedules. */
function withScheduleCount(
  summary: BidSchedulesSummary | null,
  artifactId: string,
  scheduleCount: number
): BidSchedulesSummary | null {
  if (!summary) return summary
  const byDocument = { ...summary.byDocument }
  for (const [documentId, entry] of Object.entries(byDocument)) {
    if (entry.wordFormArtifactId === artifactId) byDocument[documentId] = { ...entry, scheduleCount }
  }
  return { ...summary, byDocument }
}

type Setter = (
  patch: Partial<WordFormState> | ((state: WordFormState) => Partial<WordFormState>)
) => void

function infoPatch(info: WordFormInfo, current: string | null): Partial<WordFormState> {
  return {
    artifact: info.artifact,
    metadata: info.metadata,
    schedules: info.schedules,
    fields: info.fields,
    headings: info.headings ?? [],
    selectedScheduleId: keepSelection(current, info.schedules),
  }
}

/** Settle a GET: ready → load proposals, failed → surface, pending → keep polling. */
async function settle(
  artifactId: string,
  info: WordFormInfo,
  epoch: number,
  set: Setter,
  get: () => WordFormState
): Promise<boolean> {
  set(infoPatch(info, get().selectedScheduleId))
  if (info.metadata.ingestStatus === 'failed') return true
  if (info.metadata.ingestStatus === 'pending') return false

  try {
    const { proposals, counts } = await getFillProposals(artifactId)
    if (epoch !== pollEpoch) return true
    set({ proposals, counts })
  } catch (error) {
    if (epoch !== pollEpoch) return true
    set({ error: messageOf(error, LOAD_ERROR) })
  }
  return true
}

async function startLoad(
  artifactId: string,
  set: Setter,
  get: () => WordFormState,
  patch: Partial<WordFormState>
): Promise<void> {
  const epoch = ++pollEpoch
  set({ ...patch, artifactId, isLoading: true, error: null })

  let info: WordFormInfo
  try {
    info = await getWordForm(artifactId)
  } catch (error) {
    if (epoch !== pollEpoch) return
    set({ isLoading: false, error: messageOf(error, LOAD_ERROR) })
    return
  }
  if (epoch !== pollEpoch) return

  const settled = await settle(artifactId, info, epoch, set, get)
  if (epoch !== pollEpoch) return
  set({ isLoading: false })
  if (settled) return

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await sleep(WORD_FORM_POLL_INTERVAL_MS)
    if (epoch !== pollEpoch) return
    const next = await getWordForm(artifactId).catch(() => null)
    if (epoch !== pollEpoch) return
    if (!next) continue
    if (await settle(artifactId, next, epoch, set, get)) return
    if (epoch !== pollEpoch) return
  }
  set({ error: TIMEOUT_ERROR })
}

export const useWordFormStore = create<WordFormState>((set, get) => ({
  ...initialSession,
  documentSchedules: {},
  bidSchedules: null,
  creatingFormFor: null,

  load: async (artifactId) => {
    const isSwitch = get().artifactId !== artifactId
    await startLoad(
      artifactId,
      set,
      get,
      isSwitch ? { ...initialSession, artifactId } : {}
    )
  },

  selectSchedule: (scheduleId) => set({ selectedScheduleId: scheduleId }),

  refreshProposals: async () => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      const { proposals, counts } = await getFillProposals(artifactId)
      if (get().artifactId !== artifactId) return
      set({ proposals, counts })
    } catch (error) {
      set({ error: messageOf(error, LOAD_ERROR) })
    }
  },

  refreshExports: async () => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      const { exports } = await getWordFormExports(artifactId)
      if (get().artifactId !== artifactId) return
      set({ exports })
    } catch (error) {
      set({ error: messageOf(error, LOAD_ERROR) })
    }
  },

  decide: async (proposalId, decision, editedValue) => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      const { proposal } = await decideFillProposal(artifactId, proposalId, decision, editedValue)
      const proposals = get().proposals.map((p) => (p.id === proposal.id ? proposal : p))
      set({ proposals, counts: countProposals(proposals), error: null })
    } catch (error) {
      const reason = reasonOf(error)
      set({
        error: reason
          ? fillRefusalMessage(reason)
          : messageOf(error, "Couldn't save that decision — try again."),
      })
    }
  },

  saveManualAnswer: async (field, draft) => {
    const { artifactId, metadata } = get()
    if (!artifactId || !metadata) return FILL_REFUSED_NOT_READY
    try {
      const { proposal, counts } = await createManualDocxFill(artifactId, {
        target: docxFieldTarget(field, metadata.sourceSha256),
        value: manualFillValue(field, draft),
      })
      if (get().artifactId !== artifactId) return null
      // The new accepted answer supersedes any earlier one for this field.
      const proposals = [
        ...get().proposals.map((p) =>
          p.targetKey === proposal.targetKey && p.status === 'accepted' && p.id !== proposal.id
            ? { ...p, status: 'superseded' as const }
            : p
        ),
        proposal,
      ]
      set({ proposals, counts })
      return null
    } catch (error) {
      return fillRefusalMessage(reasonOf(error))
    }
  },

  acceptAllFromProfile: async (scheduleId) => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      // One call: the server resolves the schedule to its fields.
      await decideAllFillProposals(artifactId, 'accepted', { scheduleId, source: 'profile' })
      set({ error: null })
    } catch (error) {
      set({ error: messageOf(error, "Couldn't accept those answers — try again.") })
    }
    await get().refreshProposals()
  },

  doExport: async () => {
    const { artifactId } = get()
    if (!artifactId) return null
    set({ isExporting: true, exportProblem: null })
    let result: WordFormExportResult
    try {
      result = await exportWordForm(artifactId)
    } catch (error) {
      set({ isExporting: false, exportProblem: exportProblemOf(error) })
      // A 400 with refused answers records why on each of them.
      if (error instanceof ApiRequestError && error.statusCode === 400) {
        await get().refreshProposals()
      }
      return null
    }
    set({ isExporting: false })
    // The export stamps the answers it wrote (and the ones it refused).
    await get().refreshProposals()
    if (get().artifactId !== artifactId) return result
    set((state) => ({
      exports: [
        result.outputVersion,
        ...state.exports.filter((e) => e.id !== result.outputVersion.id),
      ],
    }))
    return result
  },

  reingest: async () => {
    const { artifactId } = get()
    if (!artifactId) return
    let metadata: WordFormMetadata
    try {
      const result = await reingestWordForm(artifactId)
      metadata = result.metadata
    } catch (error) {
      set({ error: messageOf(error, "Couldn't retry reading this form — try again.") })
      return
    }
    await startLoad(artifactId, set, get, { metadata })
  },

  correctSchedules: async (artifactId, correction) => {
    set({ isCorrecting: true, correctionError: null })
    try {
      const { schedules } = await correctFormSchedules(artifactId, correction)
      set((state) => {
        const documentSchedules = { ...state.documentSchedules }
        for (const [documentId, entry] of Object.entries(documentSchedules)) {
          if (entry.wordFormArtifactId === artifactId) {
            documentSchedules[documentId] = { ...entry, schedules }
          }
        }
        const isOpenForm = state.artifactId === artifactId
        return {
          isCorrecting: false,
          error: null,
          documentSchedules,
          bidSchedules: withScheduleCount(state.bidSchedules, artifactId, schedules.length),
          ...(isOpenForm
            ? { schedules, selectedScheduleId: keepSelection(state.selectedScheduleId, schedules) }
            : {}),
        }
      })
      return true
    } catch (error) {
      set({ isCorrecting: false, correctionError: scheduleCorrectionMessage(error) })
      return false
    }
  },

  loadBidSchedules: async (bidId, force = false) => {
    const current = get().bidSchedules
    if (current?.bidId === bidId && !force && current.status !== 'error') return
    set({
      bidSchedules: {
        bidId,
        status: 'loading',
        byDocument: current?.bidId === bidId ? current.byDocument : {},
      },
    })
    try {
      const { documents } = await getBidSchedules(bidId)
      if (get().bidSchedules?.bidId !== bidId) return
      set({
        bidSchedules: {
          bidId,
          status: 'ready',
          byDocument: Object.fromEntries(documents.map((d) => [d.documentId, d])),
        },
      })
    } catch {
      if (get().bidSchedules?.bidId !== bidId) return
      set((state) => ({
        bidSchedules: { bidId, status: 'error', byDocument: state.bidSchedules?.byDocument ?? {} },
      }))
    }
  },

  createForm: async (bidId, documentId, name) => {
    if (get().creatingFormFor) return null
    set({ creatingFormFor: documentId })
    try {
      const { artifact, existing } = await createWordForm(bidId, { sourceDocumentId: documentId, name })
      set((state) => {
        const panel = state.documentSchedules[documentId]
        return {
          creatingFormFor: null,
          bidSchedules: withFormArtifact(state.bidSchedules, documentId, artifact.id),
          documentSchedules: panel
            ? { ...state.documentSchedules, [documentId]: { ...panel, wordFormArtifactId: artifact.id } }
            : state.documentSchedules,
        }
      })
      return { artifactId: artifact.id, existing: existing === true }
    } catch (error) {
      set({ creatingFormFor: null, error: messageOf(error, CREATE_FAILED_MESSAGE) })
      return null
    }
  },

  loadDocumentSchedules: async (bidId, documentId, force = false) => {
    const existing = get().documentSchedules[documentId]
    if (existing && !force && existing.status !== 'error') return
    set((state) => ({
      documentSchedules: {
        ...state.documentSchedules,
        [documentId]: {
          status: 'loading',
          schedules: existing?.schedules ?? [],
          wordFormArtifactId: existing?.wordFormArtifactId ?? null,
        },
      },
    }))
    try {
      const info = await getDocumentSchedules(bidId, documentId)
      set((state) => ({
        documentSchedules: {
          ...state.documentSchedules,
          [documentId]: { status: 'ready', ...info },
        },
      }))
    } catch {
      set((state) => ({
        documentSchedules: {
          ...state.documentSchedules,
          [documentId]: { status: 'error', schedules: [], wordFormArtifactId: null },
        },
      }))
    }
  },

  clearError: () => set({ error: null }),

  clearExportProblem: () => set({ exportProblem: null }),

  clearCorrectionError: () => set({ correctionError: null }),

  reset: () => {
    pollEpoch++
    set({ ...initialSession })
  },
}))
